import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

/**
 * useSort — generic column-sort hook for tables.
 *
 * Usage:
 *   const { sortBy, sortDir, toggleSort, SortIcon, sortedRows } = useSort(
 *     rows,
 *     {
 *       initial: { by: "nome", dir: "asc" },
 *       getKey: (row, by) => row[by],  // optional, defaults to row[by]
 *     }
 *   );
 *   ...
 *   <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("nome")}>
 *     Nome <SortIcon col="nome" />
 *   </TableHead>
 */
export default function useSort(rows, opts = {}) {
  const { initial = { by: null, dir: "asc" }, getKey } = opts;
  const [sortBy, setSortBy] = useState(initial.by);
  const [sortDir, setSortDir] = useState(initial.dir);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    const keyFn = getKey || ((row, by) => row[by]);
    const norm = (v) => {
      if (v == null) return "";
      if (typeof v === "string") return v.toLowerCase();
      return v;
    };
    const arr = [...rows].sort((a, b) => {
      const va = norm(keyFn(a, sortBy));
      const vb = norm(keyFn(b, sortBy));
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortBy, sortDir, getKey]);

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown size={12} className="inline ml-1 opacity-30" />;
    return sortDir === "asc"
      ? <ChevronUp size={12} className="inline ml-1" style={{ color: "var(--ds-primary)" }} />
      : <ChevronDown size={12} className="inline ml-1" style={{ color: "var(--ds-primary)" }} />;
  };

  return { sortBy, sortDir, toggleSort, SortIcon, sortedRows };
}
